import { EndpointBuilder } from '@reduxjs/toolkit/dist/query/endpointDefinitions';

import { providesList } from 'services/utils';
import { Driver } from 'app/pages/DriverPage/types';
import { createTour, Tour, TourJson } from 'app/models/Tour';
import { selectAllDestinations } from 'store/slices/destinationsSlice';
import { selectAllCarriers } from 'store/slices/carriersSlice';
import { selectAllColors } from 'store/slices/colorSlice';
import { createPartOrder, PartOrderJson } from 'app/models/PartOrder';
import { addPartOrders } from 'store/slices/partOrderSlice';
import { addTours, selectTourEntities } from 'store/slices/toursSlice';
import { createDelivery } from 'app/models/Delivery';
import { addDeliveries } from 'store/slices/deliverySlice';
import { selectOrderAction } from 'app/containers/TourMap/slice';
import {
  setActiveSearch,
  setCurrentPartOrderId,
} from 'store/slices/activeContentSlice';
import {
  actions as searchFieldActions,
  searchingOrder,
} from 'app/containers/SearchField/slice';
import { toast } from 'react-toastify';
import { compareAsc, format, parseISO } from 'date-fns';
import { ConfirmationReminderResponse } from 'services/deliveryEndpoints';
import { mergeSearchDestinations } from 'services/searchDestinations';

export const tourEndpoints = (builder: EndpointBuilder<any, any, any>) => ({
  getActiveTours: builder.query<Tour[], void>({
    query: () => `/tour/`,
    providesTags: result => providesList(result, 'Tour'),
  }),
  getTour: builder.query<Tour, number>({
    query: id => `/tour/${id}/`,
    providesTags: (result, error, id) => [{ type: 'Tour', id }],
  }),
  addTour: builder.mutation<Tour, Partial<Tour>>({
    query: body => ({
      url: `/tour/`,
      method: 'POST',
      body,
    }),
    invalidatesTags: [{ type: 'Tour', id: 'LIST' }],
  }),
  updateTour: builder.mutation<Partial<Tour>, Partial<Tour>>({
    query: ({ id, ...body }) => ({
      url: `/tour/${id}/`,
      method: 'PUT',
      body,
    }),
    invalidatesTags: (result, error, { id }) => [
      { type: 'Tour', id },
      { type: 'Tour', id: 'LIST' },
      { type: 'Delivery', id: 'LIST' },
      { type: 'PartOrder', id: 'LIST' },
    ],
  }),
  deleteTour: builder.mutation<void, number>({
    query: id => ({
      url: `/tour/${id}/`,
      method: 'DELETE',
    }),
    invalidatesTags: (result, error, id) => [
      { type: 'Tour', id },
      { type: 'Tour', id: 'LIST' },
    ],
  }),
  getTourHistory: builder.query<Tour[], { start: string; end: string }>({
    query: ({ start, end }) => `/tour/history/?start=${start}&end=${end}`,
    transformResponse: (response: any[]) => {
      return response.map(tourJson => createTour(tourJson));
    },
  }),
  notifyAllCustomers: builder.mutation<unknown, number>({
    query: tourId => ({
      url: `tour/${tourId}/notify`,
      method: 'GET',
    }),
    invalidatesTags: (result, error, tourId) => [{ type: 'Tour', id: tourId }],
  }),
  sendTourConfirmationReminders: builder.mutation<
    ConfirmationReminderResponse,
    number
  >({
    query: tourId => ({
      url: `tour/${tourId}/confirmation_reminders/`,
      method: 'POST',
    }),
    invalidatesTags: (result, error, tourId) => [
      { type: 'Tour', id: tourId },
      { type: 'Delivery', id: 'LIST' },
    ],
  }),
  searchOrderTour: builder.query<void, number>({
    query: orderId => `tour/search?order=${orderId}`,
  }),
  setDriversTour: builder.mutation<void, { tour: Tour; drivers: Driver[] }>({
    query: ({ tour, drivers }) => ({
      url: `/tour/${tour.id}/set_drivers/`,
      method: 'POST',
      body: { drivers },
    }),
    invalidatesTags: (result, error, { tour }) => [
      { type: 'Tour', id: tour.id },
    ],
  }),
  searchOrder: builder.query<
    { tours: TourJson[]; orders: PartOrderJson[] },
    number
  >({
    query: orderId => `tour/search/?order=${orderId}`,
    async onQueryStarted(orderId, { dispatch, getState, queryFulfilled }) {
      let data: { tours: TourJson[]; orders: PartOrderJson[] };

      try {
        ({ data } = await queryFulfilled);
      } catch (error) {
        toast.error(`La commande ${orderId} n'a pas été trouvée`);
        dispatch(searchingOrder(false));
        return;
      }

      try {
        if (data && data.tours.length > 0) {
          const rawTours = data.tours;

          const getOrderIdFromDelivery = (
            delivery: any,
          ): number | undefined => {
            const order = delivery?.order;
            if (typeof order === 'number') return order;
            if (typeof order === 'object' && typeof order?.id === 'number')
              return order.id;
            return undefined;
          };

          const toursContainingOrder = rawTours.filter(
            tour =>
              Array.isArray(tour.orders) &&
              tour.orders.some(
                (delivery: any) => getOrderIdFromDelivery(delivery) === orderId,
              ),
          );

          const tourCandidates =
            toursContainingOrder.length > 0 ? toursContainingOrder : rawTours;

          const selectedTour = [...tourCandidates]
            .sort((a, b) => {
              const dateComp = compareAsc(parseISO(a.date), parseISO(b.date));
              if (dateComp === 0) return a.id - b.id;
              return dateComp;
            })
            .pop();

          // A "past tour" for this flow is a search result explicitly marked delivered
          // and not already loaded in the current tours store.
          // @ts-ignore
          const currentTourEntities = selectTourEntities(getState());
          const isTourAlreadyLoaded =
            selectedTour !== undefined &&
            currentTourEntities[selectedTour.id] !== undefined;

          if (selectedTour && selectedTour.delivered && !isTourAlreadyLoaded) {
            const redirectPayload = {
              date: format(parseISO(selectedTour.date), 'yyyy-MM-dd'),
              tourId: selectedTour.id,
            };
            dispatch(
              searchFieldActions.setRedirectToHistory({
                date: redirectPayload.date,
                tourId: redirectPayload.tourId,
                orderId,
              }),
            );

            const redirectStateAfterDispatch = (getState() as any)?.searchField
              ?.redirectToHistory;

            if (!redirectStateAfterDispatch && typeof window !== 'undefined') {
              const redirectPath = `/history?date=${redirectPayload.date}&tourId=${redirectPayload.tourId}&orderId=${orderId}`;
              const isElectron = Boolean((window as any)?.platform?.isElectron);
              const redirectUrl = isElectron
                ? `#${redirectPath}`
                : redirectPath;
              window.location.assign(redirectUrl);
            }

            dispatch(searchingOrder(false));
            return;
          }

          // Keep only tours that contain deliveries before model mapping.
          // Some API search responses can contain tours without `orders`, which
          // would crash `createTour` (expects an array).
          const tours = rawTours
            .filter(tour => Array.isArray(tour.orders))
            .map((item: TourJson) => createTour(item));
          dispatch(addTours(tours));

          // @ts-ignore
          const catalogDestinations = selectAllDestinations(getState());
          const destinations = mergeSearchDestinations(
            catalogDestinations,
            tours,
          );
          // @ts-ignore
          const carriers = selectAllCarriers(getState());
          // @ts-ignore
          const colors = selectAllColors(getState());

          const orders = Array.isArray(data.orders) ? data.orders : [];
          const pastOrders = orders.map(entry =>
            createPartOrder(entry, destinations, carriers, colors),
          );

          dispatch(addPartOrders(pastOrders));

          const deliveries = tours
            .map(tours =>
              tours.orders.map(delivery => createDelivery(delivery)),
            )
            .flat();
          dispatch(addDeliveries(deliveries));

          const partSearchOrder = pastOrders.filter(
            item => item.order.id === orderId,
          );
          if (partSearchOrder.length > 0) {
            dispatch(selectOrderAction(partSearchOrder[0]));
            dispatch(setCurrentPartOrderId(partSearchOrder[0].id));
            dispatch(
              setActiveSearch({
                tourIds: tours.map(e => e.id),
                partOrderIds: partSearchOrder.map(e => e.id),
              }),
            );
          }
        }
        dispatch(searchingOrder(false));
      } catch (error) {
        console.error('Error while processing searched order', error);
        toast.error(`La commande ${orderId} n'a pas pu être chargée`);
        dispatch(searchingOrder(false));
      }
    },
  }),
});
